import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ViewContainerRef, HostListener } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup, Validators, UntypedFormBuilder, FormArray, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseComponent } from '../base-component';
import { AFCertificationDomain, AF_CONSTANTS } from '../model/af.model';
import { CertificationService } from '../service/certification.service';
import { EventService } from '../service/event.service';
import { ChangeNavigationEvent, ProgressBarEvent, ValidateAndNavigateEvent, BeforeSubmitErrorEvent, SkipToMainContentEvent } from '../service/events';
import { AuthService } from '../service/auth.service';
import { KeyValue } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomValidatorsService } from '../service/custom-validators.service';
import { MatSelectionListChange } from '@angular/material/list';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ProfessionalCertificationDialogComponent } from './professional-certification-dialog/professional-certification-dialog.component';
import { FloatLabelType } from '@angular/material/form-field';

@Component({
  selector: 'app-professional-certification',
  templateUrl: './professional-certification.component.html',
  styleUrls: ['./professional-certification.component.scss']
})
export class ProfessionalCertificationComponent extends BaseComponent implements OnInit, OnDestroy {

  maxDate = new Date();
  minDate = new Date(1900, 0, 1);
  certificationData: AFCertificationDomain;
  certificationGroup: UntypedFormGroup;
  step: number = 0;
  sticky: boolean = false;
  menuPosition: any;
  border: boolean = false;
  containerWidth: number;
  professionalCertChange: boolean = false;
  listCheck: boolean = false;
  licenseIdText: string = 'License ID Number';
  float: FloatLabelType = 'always';

  @ViewChild("stickyDiv") stickyDivElement: ElementRef;
  @ViewChildren('stickyDiv', { read: ViewContainerRef }) viewContainerRefs: QueryList<any>;
  @ViewChild("addElement") addElement: ElementRef;
  @ViewChild("addButtonElement") addButtonElement: ElementRef;

  constructor(private userService: AuthService, private eventBus: EventService, private certificationService: CertificationService, private fb: UntypedFormBuilder,
    private _snackBar: MatSnackBar, private _router: Router, private customValidator: CustomValidatorsService,
    private dialog: MatDialog, public dialogRef: MatDialogRef<ProfessionalCertificationDialogComponent>) {
    super(eventBus, _router);
  }

  ngOnInit() {
    this.subscribeEvents();
    this.createGroup();
  }

  createProfessionalCertificateDialog() {
    // check this part of OUS list to show the popup
    let jobCountryCode = this.userService.getJobCountry();
    if (jobCountryCode && jobCountryCode !== 'USA') {
      this.dialogRef = this.dialog.open(ProfessionalCertificationDialogComponent, {
        ariaLabel: 'Professional Certificate Dialog',
        role: 'alertdialog',
        autoFocus: false,
        width: '550px',
        disableClose: true
      });
    }
  }

  createGroup() {
    this.certificationGroup = this.fb.group({
      afId: [],
      verifyId: [this.userService.getVerifyId()],
      bookmarkUrl: [''],
      submit: [false],
      noCertificationBand: [false],
      noCertification: [false],
      certificationList: new UntypedFormArray([])
    });
  }

  setStep(index: number) {
    this.step = index;
  }

  scroll(el: HTMLElement) {
    el.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'end' });
  }

  subscribeEvents() {
    this.certificationService.getCertificationHistory().subscribe({
      next: (data: AFCertificationDomain) => {
        this.certificationData = data;
        if (this.userService.getLicenseIdText()) {
          this.licenseIdText = this.userService.getLicenseIdText();
        }
        this.populateCertificationHistory(this.certificationData);
        this.createProfessionalCertificateDialog();
      },
      error: (error: Error) => {
        console.log(error);
      }
    });

    this.sub = this.eventBus.on(
      ValidateAndNavigateEvent,
      (event): void => {
        if (event.payload.currentLink === '/professionalCertification') {
          this.bookmarkUrl.setValue("");
          this.submit.setValue(false);
          if (event.payload.save) {
            this.submitCertificationHistory((this.certificationGroup.value), event);
          } else if (event.payload.saveAndFinishLater) {
            this.bookmarkUrl.setValue(event.payload.currentLink);
            this.submitCertificationHistory((this.certificationGroup.value), event);
          } else {
            if (this.validate()) {
              this.submitCertificationHistory((this.certificationGroup.value), event);
            } else {
              // this.eventBus.emit(new BeforeSubmitErrorEvent({}));
            }
          }
        }
      }
    );

    this.eventBus.on(
      SkipToMainContentEvent,
      (event): void => {
        if (event.payload.text && event.payload.text == "/professionalCertification") {
          let mainContentElement = document.querySelector("#main-content");//check if main-content id exists on page
          if (mainContentElement) {
            mainContentElement.scrollIntoView(true);
          }
        }
      }
    );
  }

  onFocusOutMainContent(event) {
    this.eventBus.emit(new SkipToMainContentEvent({ text: "FocusOff" }));
  }

  validateNoCertificate() {
    let channelArray: Array<string> = ['commt', 'isCurrentMemberShip', 'organizationUsState', 'organizationState', 'organizationCity', 'organizationWebsite', 'typeOfMemberShip'];

    for (let container of this.certificationGroup.get('certificationList')['controls']) {
      for (let controlKey in container.controls) {
        if (channelArray.some(x => x === controlKey)) {
          continue;
        }
        if (container.controls.hasOwnProperty(controlKey)) {
          let control = container.controls[controlKey];
          if (this.noCertification.value == 1) { //has no certificates
            control.setValidators(null);
            control.setErrors(null);
          } else {
            control.setValidators([Validators.required])
          }
          control.updateValueAndValidity();
        }
      }
    }
  }

  submitCertificationHistory(certificationHistory, event) {
    this.certificationService.submitCertificationHistory(certificationHistory).subscribe({
      next: () => {
        if (event.payload.save) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
        } else if (event.payload.saveAndFinishLater) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
          this._router.navigate(['sessionTimedOut']);
        } else {
          this.eventBus.emit(new ChangeNavigationEvent({ id: event.payload.id }));
          this._router.navigateByUrl(event.payload.nextNavLink);
        }
      },
      error: (error: any) => console.log(error)
    });
  }

  get certificationList() { return this.certificationGroup.get('certificationList') as UntypedFormArray; }
  get submit() { return this.certificationGroup.get('submit') }
  get noCertification() { return this.certificationGroup.get('noCertification') }
  get noCertificationBand() { return this.certificationGroup.get('noCertificationBand') }
  get bookmarkUrl() { return this.certificationGroup.get('bookmarkUrl') };


  populateCertificationHistory(certificationData: AFCertificationDomain) {
    this.certificationGroup.patchValue(certificationData);
    for (let address of this.certificationData.certificationList) {
      let fg: UntypedFormGroup = this.fieldsGroup();
      fg.patchValue(address);
      this.certificationList.push(fg);
    }

    if (this.certificationList.length == 0) {
      this.createComponent();
    }
  }

  createComponent() {
    let fg: UntypedFormGroup = this.fieldsGroup();
    this.certificationList.push(fg);
    this.step = this.certificationList.length - 1;
    this.submit.setValue(false);
  }

  getPercentage(certificationGroup: UntypedFormGroup): number {
    let errorCount = 0;
    let totalCount = 0;
    let channelArray: Array<string> = ['commt'];

    if (this.noCertification.value) {
      return 100;
    }

    for (let container of certificationGroup.get('certificationList')['controls']) {
      for (let controlKey in container.controls) {
        if (channelArray.some(x => x === controlKey)) {
          continue;
        }
        if (container.controls.hasOwnProperty(controlKey)) {
          if (container.controls[controlKey].validator != null) {
            totalCount += container.controls[controlKey].validator.length;
          }
          if (container.controls[controlKey].errors) {
            errorCount += Object.keys(container.controls[controlKey].errors).length;
          }
        }
      }
    }
    let percentage = ((totalCount - errorCount) / totalCount) * 100;
    return percentage;
  }

  deleteClick(i) {
    this.certificationList.removeAt(i);
    if (i == this.certificationList.length) {
      this.step = i - 1;
    }

    setTimeout(() => {
      if (this.certificationList && this.certificationList.length == 0) {
        this.addButtonElement.nativeElement.focus();
        this.border = false;
      } else {
        this.addElement.nativeElement.focus();
        this.border = true;
      }
    });
  }

  onAddCertification() {
    setTimeout(() => {
      this.addElement.nativeElement.focus();
      this.border = true;
    });
    this.createComponent();
  }


  @HostListener('document:keydown', ['$event'])
  clearBorder(keyDown: KeyboardEvent) {
    if (keyDown.key === 'Tab') {
      this.border = false;
    }
  }

  @HostListener('click', ['$event'])
  clearBorderClick() {
    this.border = false;
  }

  handleSelection(event: MatSelectionListChange) {
    event.source.deselectAll();
    event.options[0].selected = true;
    this.step = event.options[0].value;
  }

  @HostListener('window:scroll', ['$event'])
  handleScroll() {
    const windowScroll = window.pageYOffset;
    if (windowScroll >= AF_CONSTANTS.windowScroll_Lower_limit && windowScroll <= AF_CONSTANTS.windowScroll_Upper_limit_certification) {
      this.sticky = true;
    } else {
      this.sticky = false;
    }
  }

  @HostListener('window:resize', ['$event'])
  @HostListener('window:load', ['$event'])
  handleZoom() {
    const windowWidth = window.innerWidth;
    if (windowWidth <= 900) {
      this.professionalCertChange = true;
    } else {
      this.professionalCertChange = false;
    }
  }

  dropDownScreenReaderCountry(certificate: any): string {
    if (certificate.controls.organizationCountry.value) {
      return 'Selected ' + this.certificationData.countryLookup[Object.keys(this.certificationData.countryLookup).find(e => e === certificate.controls.organizationCountry.value)];
    } else {
      return 'blank';
    }
  }

  dropDownScreenReaderLicenseType(certificate: any): string {
    if (certificate.controls.licenseType.value) {
      return 'Selected ' + this.certificationData.certTypeLookup[Object.keys(this.certificationData.certTypeLookup).find(e => e === certificate.controls.licenseType.value)];
    } else {
      return 'blank';
    }
  }

  dropDownScreenReaderUsState(certificate: any): string {
    if (certificate.controls.organizationUsState.value) {
      return 'Selected ' + this.certificationData.usStateLookup[Object.keys(this.certificationData.usStateLookup).find(e => e === certificate.controls.organizationUsState.value)];
    } else {
      return 'blank';
    }
  }

  ngAfterViewInit() {
    this.handleZoom();
    this.viewContainerRefs.changes.subscribe(() => {
      if (this.viewContainerRefs.toArray().length) {
        if (this.stickyDivElement) {
          this.containerWidth = this.stickyDivElement.nativeElement.clientWidth;
        }
      }
    });

    this.certificationGroup.valueChanges.subscribe(() => {
      setTimeout(() => {
        let progress = this.getPercentage(this.certificationGroup);
        this.eventBus.emit(new ProgressBarEvent({ id: progress }));
      }, 100);

      if (this.certificationList.length != 0) {
        this.listCheck = true;
      } else {
        this.listCheck = false;
      }
    });

    // retain navigation on page refresh
    let navlink = this.userService.getNavLinks().find(e => e.routerLink === "/professionalCertification");
    this.eventBus.emit(new ChangeNavigationEvent({ id: navlink.id }));

    this.noCertification.valueChanges.subscribe(() => {
      this.validateNoCertificate();
    });
  }

  valueAscOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    return a.value.localeCompare(b.value);
  }

  valueAscOrderProfessionalLicensesFirst = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    try {
      if (a.value == "Professional Licenses") {
        return -1;
      }
      if (b.value == "Professional Licenses") {
        return 1;
      }
      return a.value.localeCompare(b.value);
    } catch (e) {
      console.log(e);
    }
  }

  fieldsGroup(): UntypedFormGroup {
    return this.fb.group({
      licenseName: ['', [Validators.required, this.customValidator.validateRequiredField]],
      licenseIdNumber: ['', [Validators.required, this.customValidator.validateRequiredField]],
      professionalOrganizationName: ['', [Validators.required, this.customValidator.validateRequiredField]],
      graduationDate: ['', [Validators.required]],
      licenseType: ['', [Validators.required]],
      organizationCountry: ['', [Validators.required]],
      licenseOriginationDate: ['', [Validators.required]],
      isCurrentMemberShip: [''],
      organizationUsState: [''],
      organizationState: [''],
      organizationCity: [''],
      organizationWebsite: [''],
      typeOfMemberShip: [''],
      commt: ['']
    })
  }

  focusTarget() {
    setTimeout(() => {
      this.addElement.nativeElement.focus();
      this.border = true;
    });
  }

  validate() {
    this.submit.setValue(true);
    this.certificationGroup.markAllAsTouched();
    if(this.certificationGroup && this.certificationGroup.invalid){
      let isInvalid = true;
      const firstInvalidField = Object.keys(this.certificationGroup.controls).find(field => this.certificationGroup.get(field)?.invalid);
      if(this.certificationGroup.controls[firstInvalidField] instanceof FormArray){
        (<FormArray>this.certificationGroup.controls[firstInvalidField]).controls.forEach((data: FormGroup)=>{
          if(data.invalid && isInvalid){
            this.userService.setFocus(data);
            isInvalid = false;
          }
        })
      } else {
        this.userService.setFocus(this.certificationGroup);
      }
    }
    return !this.certificationGroup.invalid;
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
